import { createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';
import type { PapelEscritorio } from '@/types/escritorio/escritorioTypes';
import type { CreateSetorDto, SetorFilters, UpdateSetorDto } from '@/types/setor/setorTypes';

import { setorService } from './services';

export const listarSetores = createAsyncThunk(
  'setor/listar',
  async (filters: SetorFilters | undefined, { rejectWithValue }) => {
    try {
      return await setorService.listar(filters);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const buscarSetorPorId = createAsyncThunk(
  'setor/buscarPorId',
  async (id: string, { rejectWithValue }) => {
    try {
      return await setorService.buscarPorId(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const criarSetor = createAsyncThunk(
  'setor/criar',
  async (data: CreateSetorDto, { rejectWithValue }) => {
    try {
      return await setorService.criar(data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const atualizarSetor = createAsyncThunk(
  'setor/atualizar',
  async ({ id, data }: { id: string; data: UpdateSetorDto }, { rejectWithValue }) => {
    try {
      return await setorService.atualizar(id, data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const excluirSetor = createAsyncThunk(
  'setor/excluir',
  async (id: string, { rejectWithValue }) => {
    try {
      await setorService.excluir(id);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarUsuariosSetor = createAsyncThunk(
  'setor/listarUsuarios',
  async ({ setorId, search }: { setorId: string; search?: string }, { rejectWithValue }) => {
    try {
      return await setorService.listarUsuarios(setorId, search);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarUsuariosDisponiveisSetor = createAsyncThunk(
  'setor/listarUsuariosDisponiveis',
  async ({ setorId, search }: { setorId: string; search?: string }, { rejectWithValue }) => {
    try {
      return await setorService.listarUsuariosDisponiveis(setorId, search);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const adicionarUsuarioSetor = createAsyncThunk(
  'setor/adicionarUsuario',
  async ({ setorId, usuarioId }: { setorId: string; usuarioId: string }, { rejectWithValue }) => {
    try {
      await setorService.adicionarUsuario(setorId, usuarioId);
      return { setorId, usuarioId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const removerUsuarioSetor = createAsyncThunk(
  'setor/removerUsuario',
  async ({ setorId, usuarioId }: { setorId: string; usuarioId: string }, { rejectWithValue }) => {
    try {
      await setorService.removerUsuario(setorId, usuarioId);
      return { setorId, usuarioId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarExecucoesSetor = createAsyncThunk(
  'setor/listarExecucoes',
  async ({ setorId, search }: { setorId: string; search?: string }, { rejectWithValue }) => {
    try {
      return await setorService.listarExecucoes(setorId, search);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarExecucoesDisponiveisSetor = createAsyncThunk(
  'setor/listarExecucoesDisponiveis',
  async ({ setorId, search }: { setorId: string; search?: string }, { rejectWithValue }) => {
    try {
      return await setorService.listarExecucoesDisponiveis(setorId, search);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const adicionarExecucaoSetor = createAsyncThunk(
  'setor/adicionarExecucao',
  async ({ setorId, execucaoId }: { setorId: string; execucaoId: string }, { rejectWithValue }) => {
    try {
      await setorService.adicionarExecucao(setorId, execucaoId);
      return { setorId, execucaoId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const removerExecucaoSetor = createAsyncThunk(
  'setor/removerExecucao',
  async ({ setorId, execucaoId }: { setorId: string; execucaoId: string }, { rejectWithValue }) => {
    try {
      await setorService.removerExecucao(setorId, execucaoId);
      return { setorId, execucaoId };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const listarConvitesSetor = createAsyncThunk(
  'setor/listarConvites',
  async (
    { setorId, filters }: { setorId: string; filters?: { usado?: boolean; cancelado?: boolean } },
    { rejectWithValue },
  ) => {
    try {
      return await setorService.listarConvites(setorId, filters);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const enviarConviteSetor = createAsyncThunk(
  'setor/enviarConvite',
  async (
    {
      setorId,
      data,
    }: {
      setorId: string;
      data: {
        email: string;
        papel?: Exclude<PapelEscritorio, 'MASTER_ADMIN'>;
        observacoes?: string;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      return await setorService.enviarConvite(setorId, data);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const cancelarConviteSetor = createAsyncThunk(
  'setor/cancelarConvite',
  async (conviteId: string, { rejectWithValue }) => {
    try {
      await setorService.cancelarConvite(conviteId);
      return conviteId;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);
